import { useId, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Field, Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { refundStatePresentation } from "@/features/invoicing/status";
import { PAY_COPY } from "./copy";
import { confirmRefundAddress, submitRefundAddress, type PayError } from "./pay-api";
import type { PayRefundView } from "./pay-types";
import { useIdempotency } from "./use-pay";

/**
 * Refund-address confirmation — two steps, in the order the payer has to take
 * them.
 *
 *   1. The payer types the address the refund should go to
 *      (`POST …/refunds/{refundId}/address`). The service emails a one-time
 *      code to the contact address from the declaration.
 *   2. The payer types that code (`POST …/refunds/{refundId}/address:confirm`).
 *
 * The address is **not** in effect until step 2 succeeds, and the page never
 * says otherwise: the second step is not a nicety, it is the control that stops
 * someone with the link from redirecting a refund to themselves. So there is no
 * "skip", no pre-filled code, and no path here that reports success on step 1
 * alone. The code is a secret: it is sent in the request body, never echoed
 * back into the page, and never written to a URL.
 *
 * Both steps are idempotent keyed, so a dropped connection is retryable without
 * registering a second address or consuming a second code.
 */
export function RefundPanel({ token, refund: initial }: { token: string; refund: PayRefundView }) {
  const ids = useId();
  const [refund, setRefund] = useState<PayRefundView>(initial);
  const [address, setAddress] = useState(refund.address ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<null | "address" | "confirm">(null);
  const [error, setError] = useState<PayError | null>(null);
  const [awaitingCode, setAwaitingCode] = useState(Boolean(refund.address) && !refund.confirmed);
  const addressKeys = useIdempotency("pay-refund-address");
  const confirmKeys = useIdempotency("pay-refund-confirm");

  const presentation = refundStatePresentation(refund.state);

  async function onSubmitAddress(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const trimmed = address.trim();
    if (!trimmed) {
      setError({ reasonCode: "VALIDATION_FAILED" });
      return;
    }
    setBusy("address");
    const result = await submitRefundAddress(token, refund.refundId, trimmed, {
      idempotencyKey: addressKeys.keyFor({ address: trimmed }),
    });
    setBusy(null);
    if (result.ok) {
      addressKeys.settle(true);
      setRefund(result.data);
      setAwaitingCode(true);
      return;
    }
    addressKeys.settle(result.status !== 0);
    setError(result.error);
  }

  async function onSubmitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setError({ reasonCode: "VALIDATION_FAILED" });
      return;
    }
    setBusy("confirm");
    const result = await confirmRefundAddress(token, refund.refundId, trimmed, {
      idempotencyKey: confirmKeys.keyFor({ code: trimmed }),
    });
    setBusy(null);
    if (result.ok) {
      confirmKeys.settle(true);
      setRefund(result.data);
      setCode("");
      return;
    }
    confirmKeys.settle(result.status !== 0);
    setError(result.error);
  }

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>{PAY_COPY.refundHeading}</CardTitle>
          <Badge variant={presentation.tone}>{presentation.label}</Badge>
        </div>
        <p className="mt-2 text-sm text-muted">{PAY_COPY.refundIntro}</p>

        {refund.confirmed ? (
          <div className="mt-3">
            <p className="text-sm font-semibold text-ink">{PAY_COPY.refundConfirmedTitle}</p>
            <p className="mt-1 text-sm text-muted">{PAY_COPY.refundConfirmedBody}</p>
            {refund.address && <p className="mt-2 break-all font-mono text-sm text-ink">{refund.address}</p>}
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-4">
            {refund.confirmationSentTo && (
              <p className="text-xs text-muted" role="status">
                Code sent to {refund.confirmationSentTo}
              </p>
            )}

            {!awaitingCode ? (
              <form className="flex flex-col gap-4" onSubmit={onSubmitAddress} noValidate>
                <Field label={PAY_COPY.refundAddressLabel} htmlFor={`${ids}-address`} required>
                  <Input
                    id={`${ids}-address`}
                    name="refundAddress"
                    value={address}
                    placeholder={PAY_COPY.refundAddressPlaceholder}
                    onChange={(event) => setAddress(event.target.value)}
                  />
                </Field>
                <div>
                  <Button type="submit" disabled={busy !== null}>
                    {busy === "address" ? PAY_COPY.refundSubmitting : PAY_COPY.refundSubmit}
                  </Button>
                </div>
              </form>
            ) : (
              <form className="flex flex-col gap-4" onSubmit={onSubmitCode} noValidate>
                <p className="text-xs text-muted">{PAY_COPY.refundPendingNote}</p>
                <Field
                  label={PAY_COPY.refundCodeLabel}
                  htmlFor={`${ids}-code`}
                  required
                  hint={PAY_COPY.refundCodeHint}
                >
                  <Input
                    id={`${ids}-code`}
                    name="refundCode"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                </Field>
                <div>
                  <Button type="submit" disabled={busy !== null}>
                    {busy === "confirm" ? PAY_COPY.refundConfirming : PAY_COPY.refundConfirm}
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        {error && (
          <div role="alert" className="mt-3 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">
            {error.reasonCode}
            <SupportDetails reference={error.supportReference} capability={error.capability} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
